import { ADMIN_SECTIONS, adminNav } from '../modules.js';

/*
 * The admin sidebar and the command palette share one source: every module's `adminNav` entries
 * (web/src/modules.js), grouped into ADMIN_SECTIONS. An entry whose section is unknown (an addon
 * written for an older layout) goes under System, so its page stays reachable.
 */

/** Pages the palette offers that have no sidebar item of their own: `[path, label, permission, under]`. */
export const ADMIN_COMMANDS = [
  ['/admin/clients/new', 'Add client', 'admin.clients.create', '/admin/clients'],
  ['/admin/clients/setup', 'Client groups and fields', 'admin.clients.update', '/admin/clients'],
  ['/admin/invoices/new', 'New invoice', 'admin.invoices.create', '/admin/invoices'],
  ['/admin/servers/groups', 'Server groups', 'admin.servers.read', '/admin/servers'],
  ['/admin/account', 'My account', null, null],
];

/** Other words a page is found by in the palette. */
const KEYWORDS = {
  '/admin/billing/gateways': ['Stripe', 'Bank transfer', 'Payments'],
  '/admin/billing/tax': ['VAT', 'Tax rules'],
  '/admin/billing/promotions': ['Promo codes', 'Coupons', 'Discounts'],
  '/admin/billing/automation': ['Cron', 'Daily run'],
  '/admin/activity': ['Audit', 'Log'],
  '/admin/mail/log': ['Sent emails'],
  '/admin/system': ['Status', 'Health', 'Version'],
  '/admin/content/kb': ['Knowledge base'],
  '/admin/content/pages': ['Custom pages', 'Terms', 'Menu'],
  '/admin/tasks': ['To-do', 'Reminders'],
  '/admin/calendar': ['Renewals', 'Due dates', 'Expiry'],
  '/admin/catalog/groups': ['Product groups'],
  '/admin/catalog/options': ['Product options', 'Configurable options'],
};

/**
 * The sections with the entries `can` (and `addonOn`) allow, in order; empty sections are dropped.
 * Each section is `{ key, label, flat?, items: [{ to, label, permission }] }`.
 */
export function permittedSections(can, { items = adminNav(), sections = ADMIN_SECTIONS, show = () => true } = {}) {
  const known = new Set(sections.map((s) => s.key));
  const visible = items.filter((i) => can(i.permission) && show(i));
  return sections
    .map((s) => ({
      ...s,
      items: visible.filter((i) => (known.has(i.section) ? i.section : 'system') === s.key),
    }))
    .filter((s) => s.items.length > 0);
}

/** The key of the section whose item is the longest prefix of `pathname`, or null. */
export function sectionOf(pathname, sections) {
  let best = null;
  let length = 0;
  for (const s of sections) {
    for (const { to } of s.items) {
      if ((pathname === to || pathname.startsWith(`${to}/`)) && to.length > length) {
        best = s.key;
        length = to.length;
      }
    }
  }
  return best;
}

const storageKey = (staffId) => `octbill.nav.open.${staffId ?? 'anon'}`;

/** The sections this staff member opened by hand, from localStorage; [] when storage is unusable. */
export function readOpened(staffId, storage = globalThis.localStorage) {
  try {
    const value = JSON.parse(storage?.getItem(storageKey(staffId)) ?? '[]');
    return Array.isArray(value) ? value.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/** Remembers the opened sections; storage errors (private mode, quota) are ignored. */
export function writeOpened(staffId, keys, storage = globalThis.localStorage) {
  try {
    if (keys.length) storage?.setItem(storageKey(staffId), JSON.stringify(keys));
    else storage?.removeItem(storageKey(staffId));
  } catch {
    // Not remembered: the sidebar still works for this visit.
  }
}

/**
 * Every page the command palette offers: the dashboard, each permitted sidebar page (with its
 * section as the hint) followed by the commands under it. Each is `{ label, to, hint, keywords }`.
 */
export function paletteCommands(sections, can) {
  const out = [{ label: 'Dashboard', to: '/admin', hint: 'Admin', keywords: ['Home'] }];
  for (const s of sections) {
    for (const item of s.items) {
      out.push({ label: item.label, to: item.to, hint: s.label, keywords: KEYWORDS[item.to] ?? [] });
      for (const [to, label, perm, under] of ADMIN_COMMANDS)
        if (under === item.to && can(perm)) out.push({ label, to, hint: s.label, keywords: [] });
    }
  }
  for (const [to, label, perm, under] of ADMIN_COMMANDS)
    if (!under && can(perm)) out.push({ label, to, hint: 'Admin', keywords: [] });
  return out;
}

/** Entries whose label, section or keywords contain every word of `query` (case-insensitive). */
export function matchCommands(commands, query) {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return commands;
  return commands.filter((c) => {
    const hay = [c.label, c.hint, ...(c.keywords ?? [])].join(' ').toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}
